import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Package,
  Warehouse,
  TrendingUp,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  DollarSign,
  ShoppingCart,
  Bell,
  AlertCircle,
  Mail,
  Send,
  Settings,
  Loader2
} from 'lucide-react';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertSettings, setAlertSettings] = useState(null);
  const [alertEmail, setAlertEmail] = useState('');
  const [alertEmails, setAlertEmails] = useState([]);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);
  const [showAlertSettings, setShowAlertSettings] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchAlertSettings();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertSettings = async () => {
    try {
      const response = await api.get('/alerts/settings');
      setAlertSettings(response.data);
      setAlertEnabled(response.data.low_stock_email_enabled || false);
      setAlertEmails(response.data.alert_emails || []);
    } catch (error) {
      // Not an admin or no settings yet — that's fine
    }
  };

  const saveAlertSettings = async () => {
    setSavingAlerts(true);
    try {
      await api.put('/alerts/settings', {
        low_stock_email_enabled: alertEnabled,
        alert_emails: alertEmails
      });
      toast.success('Alert settings saved');
      fetchAlertSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingAlerts(false);
    }
  };

  const addAlertEmail = () => {
    const email = alertEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (alertEmails.includes(email)) {
      toast.error('Email already added');
      return;
    }
    setAlertEmails([...alertEmails, email]);
    setAlertEmail('');
  };

  const removeAlertEmail = (emailToRemove) => {
    setAlertEmails(alertEmails.filter(e => e !== emailToRemove));
  };

  const sendTestAlert = async () => {
    setTestingAlert(true);
    try {
      const res = await api.post('/alerts/test-low-stock');
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test alert');
    } finally {
      setTestingAlert(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency',
      currency: 'JMD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value).replace('JMD', 'J$');
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Total SKUs',
      value: stats?.total_skus || 0,
      icon: Package,
      color: 'text-[#007AFF]',
      bg: 'bg-[#007AFF]/10'
    },
    {
      title: 'Warehouses',
      value: stats?.total_warehouses || 0,
      icon: Warehouse,
      color: 'text-[#34C759]',
      bg: 'bg-[#34C759]/10'
    },
    {
      title: 'Stock Value',
      value: formatCurrency(stats?.total_stock_value || 0),
      icon: TrendingUp,
      color: 'text-[#007AFF]',
      bg: 'bg-[#007AFF]/10'
    },
    {
      title: 'Low Stock Items',
      value: stats?.low_stock_count || 0,
      icon: AlertTriangle,
      color: stats?.low_stock_count > 0 ? 'text-[#FF3B30]' : 'text-[#34C759]',
      bg: stats?.low_stock_count > 0 ? 'bg-[#FF3B30]/10' : 'bg-[#34C759]/10'
    }
  ];

  const todaySales = stats?.today_sales || { count: 0, revenue: 0, profit: 0, units_sold: 0 };
  const lowStockItems = stats?.low_stock_items || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Page Header */}
      <div>
        <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">DASHBOARD</h1>
        <p className="text-[#0A0A0A]/60 mt-1">Overview of your inventory operations</p>
      </div>

      {/* Low Stock Alert Banner */}
      {lowStockItems.length > 0 && (
        <Card className="border-2 border-[#FF3B30] shadow-none bg-[#FF3B30]/5" data-testid="low-stock-alert">
          <CardHeader className="pb-2">
            <CardTitle className="text-heading text-lg flex items-center gap-2 text-[#FF3B30]">
              <Bell className="w-5 h-5 animate-pulse" />
              Low Stock Alert - {lowStockItems.length} item(s) need attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {lowStockItems.slice(0, 5).map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    item.severity === 'critical' ? 'bg-[#FF3B30]/10' : 'bg-[#FF9500]/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {item.severity === 'critical' ? (
                      <AlertCircle className="w-5 h-5 text-[#FF3B30]" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-[#FF9500]" />
                    )}
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-xs text-[#0A0A0A]/60">
                        <span className="font-mono">{item.sku}</span> • {item.warehouse_name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={item.severity === 'critical' ? 'bg-[#FF3B30] text-white' : 'bg-[#FF9500] text-white'}>
                      {item.quantity_on_hand} left
                    </Badge>
                    <p className="text-xs text-[#0A0A0A]/50 mt-1">
                      Reorder at: {item.reorder_point}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {lowStockItems.length > 5 && (
              <p className="text-center text-sm text-[#0A0A0A]/60 mt-3">
                +{lowStockItems.length - 5} more items need attention
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Link to="/app/inventory">
                <Button variant="outline" size="sm">View All Inventory</Button>
              </Link>
              <Link to="/app/purchase-orders">
                <Button size="sm" className="bg-[#007AFF]">Create Purchase Order</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low Stock Email Alert Settings */}
      <Card className="border border-[#E5E5EA] shadow-none" data-testid="alert-settings-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#FF9500]" />
            Low Stock Email Alerts
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setShowAlertSettings(!showAlertSettings)} data-testid="toggle-alert-settings-btn">
            <Settings className="w-4 h-4 mr-1" />
            {showAlertSettings ? 'Hide' : 'Configure'}
          </Button>
        </CardHeader>
        {!showAlertSettings ? (
          <CardContent className="pt-0">
            <div className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${alertEnabled ? 'bg-[#34C759]' : 'bg-[#8E8E93]'}`} />
                <span className="text-sm text-[#0A0A0A]/70">
                  {alertEnabled ? `Active — sending to ${alertEmails.length} email(s) every 12 hours` : 'Disabled — enable to get email alerts when stock is low'}
                </span>
              </div>
              {alertSettings?.last_alert_sent && (
                <span className="text-xs text-[#0A0A0A]/40">Last sent: {new Date(alertSettings.last_alert_sent).toLocaleDateString()}</span>
              )}
            </div>
          </CardContent>
        ) : (
          <CardContent className="pt-0 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Enable Email Alerts</Label>
                <p className="text-xs text-[#0A0A0A]/50 mt-0.5">Receive an email when items drop below reorder point</p>
              </div>
              <Switch checked={alertEnabled} onCheckedChange={setAlertEnabled} data-testid="alert-toggle" />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Alert Recipients</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addAlertEmail()}
                  className="flex-1"
                  data-testid="alert-email-input"
                />
                <Button variant="outline" onClick={addAlertEmail} data-testid="add-alert-email-btn">Add</Button>
              </div>
              {alertEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {alertEmails.map((email, idx) => (
                    <Badge key={idx} variant="secondary" className="py-1 px-3 flex items-center gap-1.5">
                      {email}
                      <button onClick={() => removeAlertEmail(email)} className="ml-1 text-[#FF3B30] hover:text-[#FF3B30]/80 text-xs font-bold">&times;</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-between pt-2 border-t border-[#E5E5EA]">
              <Button variant="outline" size="sm" onClick={sendTestAlert} disabled={testingAlert || alertEmails.length === 0} data-testid="test-alert-btn">
                {testingAlert ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Send Test Alert
              </Button>
              <Button size="sm" className="bg-[#007AFF]" onClick={saveAlertSettings} disabled={savingAlerts} data-testid="save-alert-settings-btn">
                {savingAlerts ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Save Settings
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Today's Sales Summary */}
      <Card className="border-2 border-[#34C759] shadow-none bg-gradient-to-r from-[#34C759]/5 to-[#007AFF]/5" data-testid="today-sales-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#34C759]" />
            Today's Sales Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-white/60 rounded-lg">
              <p className="text-overline text-[#0A0A0A]/50 mb-1">Sales</p>
              <p className="text-3xl font-mono font-bold text-[#0A0A0A]">{todaySales.count}</p>
            </div>
            <div className="text-center p-4 bg-white/60 rounded-lg">
              <p className="text-overline text-[#0A0A0A]/50 mb-1">Revenue</p>
              <p className="text-2xl font-mono font-bold text-[#007AFF]">{formatCurrency(todaySales.revenue)}</p>
            </div>
            <div className="text-center p-4 bg-white/60 rounded-lg">
              <p className="text-overline text-[#0A0A0A]/50 mb-1">Profit</p>
              <p className={`text-2xl font-mono font-bold ${todaySales.profit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                {formatCurrency(todaySales.profit)}
              </p>
            </div>
            <div className="text-center p-4 bg-white/60 rounded-lg">
              <p className="text-overline text-[#0A0A0A]/50 mb-1">Units Sold</p>
              <p className="text-3xl font-mono font-bold text-[#0A0A0A]">{formatNumber(todaySales.units_sold)}</p>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Link to="/app/sales">
              <Button variant="outline" size="sm" className="text-[#34C759] border-[#34C759] hover:bg-[#34C759]/10">
                <DollarSign className="w-4 h-4 mr-2" />
                Record New Sale
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, index) => (
          <Card key={index} className="border border-[#E5E5EA] shadow-none">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-overline text-[#0A0A0A]/50 mb-2">{kpi.title}</p>
                  <p className="kpi-number text-3xl text-[#0A0A0A]">{kpi.value}</p>
                </div>
                <div className={`w-10 h-10 ${kpi.bg} rounded-sm flex items-center justify-center`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Activity & Top Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Movements */}
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#007AFF]" />
              Recent Movements
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {stats?.recent_movements?.length > 0 ? (
              <div className="divide-y divide-[#E5E5EA]">
                {stats.recent_movements.slice(0, 5).map((movement, index) => (
                  <div key={index} className="flex items-center justify-between px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${
                        movement.quantity > 0 ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'
                      }`}>
                        {movement.quantity > 0 ? (
                          <ArrowUpRight className="w-4 h-4 text-[#34C759]" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-[#FF3B30]" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0A0A0A]">{movement.item_name}</p>
                        <p className="text-xs text-[#0A0A0A]/50 font-mono">{movement.item_sku}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-xs font-mono">
                        {movement.type}
                      </Badge>
                      <p className={`text-sm font-mono mt-1 ${movement.quantity > 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                        {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-[#0A0A0A]/50">
                No recent movements
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Moved Items */}
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#007AFF]" />
              Top Moved Items (7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {stats?.top_moved_items?.length > 0 ? (
              <div className="divide-y divide-[#E5E5EA]">
                {stats.top_moved_items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between px-6 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#F5F5F7] rounded-sm flex items-center justify-center text-xs font-mono text-[#0A0A0A]/60">
                        {index + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-[#0A0A0A]">{item.name}</p>
                        <p className="text-xs text-[#0A0A0A]/50 font-mono">{item.sku}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-[#0A0A0A]">{item.movements} moves</p>
                      <p className="text-xs text-[#0A0A0A]/50 font-mono">{formatNumber(item.quantity)} units</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-[#0A0A0A]/50">
                No movement data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-overline text-[#0A0A0A]/50 mb-1">Total Units</p>
              <p className="text-2xl font-mono font-medium text-[#0A0A0A]">{formatNumber(stats?.total_units || 0)}</p>
            </div>
            <div>
              <p className="text-overline text-[#0A0A0A]/50 mb-1">Movements This Week</p>
              <p className="text-2xl font-mono font-medium text-[#0A0A0A]">{formatNumber(stats?.movements_this_week || 0)}</p>
            </div>
            <div>
              <p className="text-overline text-[#0A0A0A]/50 mb-1">Active Items</p>
              <p className="text-2xl font-mono font-medium text-[#0A0A0A]">{formatNumber(stats?.total_skus || 0)}</p>
            </div>
            <div>
              <p className="text-overline text-[#0A0A0A]/50 mb-1">Stock Locations</p>
              <p className="text-2xl font-mono font-medium text-[#0A0A0A]">{formatNumber(stats?.total_warehouses || 0)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
